import { Hex } from 'viem'

import { SwapDirection, SwapRouter } from '~/components/forms/swap/utils'
import { SupportedChainId } from '~/constants/chains'
import { Address, EvmAddress } from '~/utils/address'
import { TransactionTitleChunk } from '~/utils/smartAccounts/titleChunks'

/**
 * @deprecated - Use `EvmAddress.Type` or `Address.Type` from `/utils/address` (which depends on how restrictive you'd like the address to be)
 */
export type IAddress = string // This has been changed from `0x${string}` to string to make migration to alt-L1 supported addresses easier

export type VmType = 'evm' | 'svm'

export type IAccount = {
  address: IAddress
  chainId: SupportedChainId
}

export type OrgPasskey = BackendPasskey & {
  displayName: string
  user: {
    id: string
    email: string
  }
}

export enum OrgMemberRole {
  Owner = 'OWNER',
  Member = 'MEMBER',
}

export type OrgMember = {
  id: string
  role: OrgMemberRole
  email: string
  createdAt: string
  displayName?: string
  profilePhotoUrl?: string
}

export type OrgInvitation = {
  id: string
  email: string
  role: OrgMemberRole
  createdAt: string
}

export type ReferralCode = {
  id: string
  createdAt: string
  code: string
  referrerEmail: string
  referrerDisplayName?: string
}

export type OnboardingStep = 'MEMBERS' | 'PASSKEYS' | 'COMPLETED'

export type SmartAccountsSetupStepConfig = {
  breadcrumbs?: { label: string; index: number }
  title?: string
}

export const SmartAccountsSetupStep = {
  TEAM: 'TEAM',
  PROFILE: 'PROFILE',
  OWNERS: 'OWNERS',
  TREASURY: 'TREASURY',
  RECOVERY: 'RECOVERY',
  REVIEW: 'REVIEW',
  COMPLETED: 'COMPLETED',
  SKIPPED: 'SKIPPED',
} as const

export type SmartAccountsSetupStep =
  (typeof SmartAccountsSetupStep)[keyof typeof SmartAccountsSetupStep]

export type SmartAccountsSetupAccount = 'ROOT' | 'TREASURY' | 'OPERATING'

type CompliantPaymentOrg = {
  id: string
  name: string
  logoUrl: string
  isCompliant: boolean
}

export type CompliantPaymentRelation = {
  id: string
  createdAt: Date
  payerOrg: CompliantPaymentOrg
  payeeOrg?: CompliantPaymentOrg
  payeeInvitedUserId?: string
  payeeInvitedEmail?: string
  address?: IAddress
  chains?: number[]
  usdTotal?: number
  hasAcceptedInvitation?: boolean
  isVerified?: boolean
  isUSBased?: boolean
  isIndividual?: boolean
  verifiedAt?: string
}

export type BridgeVirtualAccount = {
  id: string
  destinationAddress: IAddress
  destinationToken: IAddress
  destinationChainId: number
  sourceData: BridgeVirtualAccountSourceData
}

export type BridgeLocale = 'us' | 'eu'

export type BridgeCustomerLocale = {
  locale: BridgeLocale
  destinationCurrency: string
}

export type AuthOrg = {
  id: string
  createdAt: string
  name: string
  logoUrl: string
  isPersonal: boolean
  members: OrgMember[]
  passkeys: OrgSigner[]
  invitations: OrgInvitation[]
  onboardingStep?: OnboardingStep // @deprecated - use smartAccountSetup.currentStep instead
  smartAccountSetup?: {
    id: string
    currentStep: SmartAccountsSetupStep
    authenticators: Array<{
      id: string
      setupAccount: SmartAccountsSetupAccount
      authenticatorId: string
    }>
    eoaSigners: Array<{
      id: string
      setupAccount: SmartAccountsSetupAccount
      name?: string
      address: string
      keySlot?: number
      email?: string
    }>
    thresholds: Array<{
      id: string
      setupAccount: SmartAccountsSetupAccount
      threshold: number
      expectedSigners: number
    }>
    chainIds: number[]
    constants: {
      ROOT_CREATION: { NEEDED_EOA_SIGNERS: number }
      TREASURY_CREATION: { NEEDED_PASSKEYS: number }
      NETWORKS_SELECTION: { NEEDED_PASSKEYS: number }
    }
  }
  smartAccountsChainIds: number[]
  isArchived: boolean
  bridge?: {
    customerId: string
    addressCollectionNeeded: boolean
    onRampPaused: boolean
    kyc: {
      kycType: 'individual' | 'business'
      kycLinkId: string
      tosLinkUrl: string
      tosStatus: string
      tosUpdatedAt: Date
      kycLinkUrl: string
      kycStatus: string
      kycUpdatedAt: Date
    }
    customerLocales: BridgeCustomerLocale[]
    virtualAccounts: BridgeVirtualAccount[]
    externalAccounts: {
      externalAccountId: string
      name: string
      accountNumberLastFour: string
      source: 'internal' | 'external'
      liquidationAddress: IAddress
      liquidationToken: IAddress
      liquidationChainId: number
    }[]
  }
  requireMemo: boolean
}

export type OrgComplianceData = {
  isVerified: boolean
  payees: CompliantPaymentRelation[]
  payers: CompliantPaymentRelation[]
}

export type BridgeVirtualAccountSourceData = {
  paymentRails: string[]
  currency: string
  bankName: string
  bankAddress: string
  bankRoutingNumber?: string
  bankAccountNumber?: string
  bankBeneficiaryName?: string
  bankBeneficiaryAddress?: string
  bic?: string
  iban?: string
}

export type OrgSmartAccountsSetup = NonNullable<AuthOrg['smartAccountSetup']>

export type SmartAccountSetupResponse = AuthOrg['smartAccountSetup']

export type AuthUserOrg = Pick<
  AuthOrg,
  'id' | 'name' | 'logoUrl' | 'createdAt' | 'isPersonal' | 'isArchived'
> & {
  role: OrgMemberRole
  setupCurrentStep?: SmartAccountsSetupStep
}

export type AuthUser = {
  id: string
  email: string
  displayName?: string
  profilePhotoUrl: string
  notificationsEnabled: boolean
  createdAt: string
  lastSeen: string
  isSplitsAdmin?: boolean
  orgs: AuthUserOrg[]
  personalOrg: AuthUserOrg
  passkeys: BackendPasskey[]
  eoaSigners: BackendUserEoaSigner[]
  splitsConnectDetectedAt: string | null
  extensionsPageViewedAt: string | null
}

export enum UserFlagKey {
  SPLITS_CONNECT_DETECTED = 'SPLITS_CONNECT_DETECTED',
  EXTENSIONS_PAGE_VIEWED = 'EXTENSIONS_PAGE_VIEWED',
}

export type UserFlags = Pick<
  AuthUser,
  'splitsConnectDetectedAt' | 'extensionsPageViewedAt'
>

export type AuthUserOrgWithPersona = AuthUserOrg & {
  compliance?: {
    isVerified: boolean
    isUSBased: boolean
    isIndividual: boolean
    name: string
  }
}

export type BackendNFTBlocklistEntry = {
  id: string
  blockType: NFTBlockType
  chainId: string
  address: IAddress
  tokenId?: string
  createdAt: string
  tokenType: 'ERC721' | 'ERC1155'
  name: string | undefined
  symbol: string | undefined
}

export type BackendToken = IAccount & {
  id: string
  tokenId: string
  decimals: number
  symbol: string
  createdAt?: string
}

export type BackendPasskey = {
  id: string
  name: string
  createdAt: string
  lastVerifiedAt?: string
  providerIconDark?: string
  providerIconLight?: string
}

export type BackendUserEoaSigner = {
  id: string
  address: string
  name: string | null
  email: string | null
  createdAt: string
  lastVerifiedAt: string | null
  accounts: Array<{
    id: string
    address: string
    name: string
    orgId: string
    orgName: string
  }>
}

export enum SmartAccountType {
  DAIMO_FORK = 'DAIMO_FORK',
  SPLITS_VAULT_V1 = 'SPLITS_VAULT_V1',
}

export enum SmartAccountRole {
  ROOT = 'ROOT',
  TREASURY = 'TREASURY',
  OPERATING = 'OPERATING',
  AUTOMATION = 'AUTOMATION',
}

export type EOASigner = {
  id: string
  address: string
  createdAt: string
  signedAt?: string
  updatedAt?: string
  lastVerifiedAt?: string
  email?: string
  userId?: string
}

export type OrgEOASigner = EOASigner & {
  name?: string
}

export type Signer = {
  id: string
  isArchived: boolean
  createdAt: string
  signedAt?: string
}

export type OrgSigner = Signer & {
  name?: string
  displayName?: string
  lastVerifiedAt?: string
  providerIconDark?: string
  providerIconLight?: string
  user?: {
    id: string
    email: string
    profilePhotoUrl?: string
    displayName?: string
  }
}

export type OrgSmartAccount = {
  id: string
  name: string
  createdAt: string
  updatedAt: string
  orgId: string
  address: IAddress
  threshold: number
  type: SmartAccountType
  role: SmartAccountRole
  signers: OrgSigner[]
  eoaSigners: OrgEOASigner[]
  hasBlockingTransaction: boolean
  isPublicSmartAccount: false
  isArchived: boolean
  chainIds: number[]
  smartAccountModules: {
    chainId: number
    moduleAddress: IAddress
    isEnabled: boolean
    pausedUntil?: string | null
  }[]
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PUBLIC_SMART_ACCOUNT_FIELDS = [
  'id',
  'createdAt',
  'updatedAt',
  'address',
  'threshold',
  'orgId',
  'type',
  'chainIds',
] satisfies Array<keyof OrgSmartAccount>

export type PublicSmartAccount = Pick<
  OrgSmartAccount,
  (typeof PUBLIC_SMART_ACCOUNT_FIELDS)[number]
> & {
  isPublicSmartAccount: true
  signers: Signer[]
  eoaSigners: EOASigner[]
}

export type BackendSmartAccount = PublicSmartAccount | OrgSmartAccount

export enum TransactionStatus {
  Drafted = 'DRAFTED',
  Created = 'CREATED',
  Queued = 'QUEUED',
  Approved = 'APPROVED',
  Expired = 'EXPIRED',
  Cancelled = 'CANCELLED',
  Submitted = 'SUBMITTED',
  Succeeded = 'SUCCEEDED',
  Failed = 'FAILED',
}

export type BackendTransactionCall = {
  to: IAddress
  value: bigint
  data: string
}

export type BackendTransactionDecodedCall = BackendTransactionCall & {
  function?: string
  args?: {
    name: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any
  }[]
}

export enum BasicTransactionType {
  Batch = 'Batch',
  Transfer = 'Transfer',
  Swap = 'Swap',
  Bridge = 'Bridge',
  Earn = 'Earn',
  WalletConnect = 'WalletConnect',
  SplitsConnect = 'SplitsConnect',
  UpdateSigners = 'UpdateSigners',
  AddNetworks = 'AddNetworks',
  RecoverAccount = 'RecoverAccount',
  Claim = 'Claim',
  CustomTxn = 'CustomTxn',
}

export enum MerkleTransactionCallType {
  AddSigner = 'AddSigner',
  RemoveSigner = 'RemoveSigner',
  RemoveEOASigner = 'RemoveEOASigner',
  UpdateThreshold = 'UpdateThreshold',
  CreateAccount = 'CreateAccount',
}

export enum MerkleTransactionUpdateNetworksType {
  UpdateNetworks = 'UpdateNetworks',
}

type MerkleTransactionCallData =
  | {
      type: MerkleTransactionCallType.AddSigner
      authenticatorId: string
      email: string
      name: string
      keySlot: number
    }
  | {
      type: MerkleTransactionCallType.RemoveSigner
      authenticatorId: string
      email: string
      name: string
    }
  | {
      type: MerkleTransactionCallType.RemoveEOASigner
      eoaSignerId: string
      address: string
      keySlot: number
    }
  | {
      type: MerkleTransactionCallType.UpdateThreshold
      oldThreshold: number
      newThreshold: number
    }
  | {
      type: MerkleTransactionCallType.CreateAccount
      address: IAddress
    }

type MerkleTransactionData =
  | MerkleTransactionCallData
  | {
      type: MerkleTransactionUpdateNetworksType.UpdateNetworks
      newChainIds: number[]
      addedChainIds?: number[]
      stateSyncs: {
        chainId: number
        smartAccountAddress: IAddress
        calls: MerkleTransactionCallData[]
      }[]
    }

export enum TransactionFailureReason {
  UNKNOWN = 'UNKNOWN',
  BUNDLER_STATUS_REJECTED = 'BUNDLER_STATUS_REJECTED',
  BUNDLER_STATUS_REVERTED = 'BUNDLER_STATUS_REVERTED',
  BUNDLER_STATUS_FAILED = 'BUNDLER_STATUS_FAILED',
  BUNDLER_RECEIPT_FAILED = 'BUNDLER_RECEIPT_FAILED',
  BUNDLER_STATUS_NOT_FOUND = 'BUNDLER_STATUS_NOT_FOUND',
}

// The pot that covered a transaction's gas, or the account itself.
export type GasPaidBy = 'plan' | 'gas_balance' | 'self'

type CommonOrgTransactionProperties = {
  id: string
  isSplitsTransaction: true
  gasPaidBy: GasPaidBy
  createdAt: string
  updatedAt: string
  submittedAt: string
  transactionTime: string
  name: string
  chainIds: number[]
  smartAccountId: string
  status: TransactionStatus
  failureReason?: TransactionFailureReason
  revertReason?: string | null
  revertReasonRaw?: string | null
  isBlocking: boolean
  options: string
  memo: string | null
  properties: Record<string, unknown> | null
  createdByUserId: string | null
  submittedByUserId: string | null
  cancelledByUserId: string | null
  orgId: string
  createdByUserEmail: string | null
  submittedByUserEmail: string | null
  smartAccountType: SmartAccountType
  smartAccountRole?: SmartAccountRole
  smartAccountAddress: IAddress
  isPersonalOrg: boolean
  chainId: number
  calls: {
    to: `0x${string}`
    value: string
    data: `0x${string}`
  }[]
  statusOrder?: number
  childTransactions: Array<OrgRegularTransaction>
  signers: OrgSigner[]
  eoaSigners: OrgEOASigner[]
  isPublicTransaction: false
  simulation?: { simulationURL: string }
  decodedCalls?: Array<BackendTransactionDecodedCall>
  isOfframp: boolean
  usdValue?: string
  usdDisplayValue?: string
  usdNetValue?: string | null
  displayTokenAddress?: string | null
  displayChainId?: number | null
  displayTokenAmount?: string | null
  bridgeExternalAccountName?: string | null
  titleChunks: TransactionTitleChunk[] | null
  transactionMetadata?: MetadataPayloads[]
  batchOperations?: BatchOperation[] | null
  transactionRecipients?: {
    recipientAddress: string
    endTokenAddress: string
    endChainId: number
    expectedAmount: string
    actualAmount: string | null
    sequence: number
    compliantPaymentRelationId?: string
  }[]
}

export type TransactionRecipient = {
  address: IAddress
  amount: string
  compliantPaymentRelationId?: string
}

export type TransferData = {
  recipients?: TransactionRecipient[]
  tokenAddress?: string
  nftAddress?: string
  nftTokenId?: string
  nftSymbol?: string
  nftName?: string
}

export type BankTransferData = {
  /** Offramp fee percentage at time of transaction (e.g. 0.25 for 0.25%) */
  feePercent: number
}

export type SwapData = {
  buyTokenAddress: string
  buyAmount: string
  sellAmount: string
  sellTokenAddress: string
  direction?: SwapDirection
  provider?: SwapRouter
  // String percentage (e.g. "0.5" for 0.5%) matching the form-state convention.
  slippage?: string
  private?: boolean
}

export const BridgeProviderType = {
  RELAY: 'RELAY',
  NEAR_INTENTS: 'NEAR_INTENTS',
} as const
export type BridgeProviderType =
  (typeof BridgeProviderType)[keyof typeof BridgeProviderType]

export type BridgeData = {
  destinationChainId: number
  sellAmount: string
  sellTokenAddress: string
  providerTransactionId?: string
  providerType?: BridgeProviderType
  private?: boolean
  status?: 'SUCCESS' | 'FAILURE' | 'PENDING' | null
  buyTokenAddress?: string
  buyAmount?: string
  destinationTransactionHash?: string | null
  refundTransactionHash?: string | null
  failureReason?: string | null
  destinationTransaction?: {
    hash: string
    time: string
  }
  refundTransaction?: {
    hash: string
    time: string
    failureReason?: string
  }
}

export const EarnAction = {
  DEPOSIT: 'DEPOSIT',
  WITHDRAW: 'WITHDRAW',
} as const
export type EarnAction = (typeof EarnAction)[keyof typeof EarnAction]

export type EarnData = {
  chainId: number
  vault: string
  shares: string
  asset: string
  assets: string
  action: EarnAction
}

export enum ClaimType {
  Vesting = 'Vesting',
}

export type ClaimData = {
  tokenAddress: string
  tokenAmount: string
  chainId: number
  protocol: string
  claimType: ClaimType
}

export type PositionClaimData = {
  tokenPositions: Array<{
    tokenAddress: string
    tokenAmount: string
    chainId: number
  }>
  protocol: string
}

export type CustomCallData = {
  calls: TransactionDisplayCall[]
}

export type TransactionDisplayCall = {
  to: IAddress
  functionSignature?: string
  args?: Array<{
    name: string | undefined
    value: unknown
  }>
  calldata: string
  value: string
  contractName?: string
}

export enum MetadataType {
  Earn = 'earn',
  Bridge = 'bridge',
  Swap = 'swap',
  Transfer = 'transfer',
  Claim = 'claim',
  CustomCall = 'custom_call',
  BankTransfer = 'bank_transfer',
  PositionClaim = 'position_claim',
}

export type MetadataPayloads = { operationId?: string } & (
  | (EarnData & { type: MetadataType.Earn })
  | (BridgeData & { type: MetadataType.Bridge })
  | (SwapData & { type: MetadataType.Swap })
  | (TransferData & { type: MetadataType.Transfer })
  | (ClaimData & { type: MetadataType.Claim })
  | (CustomCallData & { type: MetadataType.CustomCall })
  | (BankTransferData & { type: MetadataType.BankTransfer })
  | (PositionClaimData & { type: MetadataType.PositionClaim })
)

export type BatchOperation = {
  operationId: string
  name: string
  walletConnectAppName?: string
  walletConnectAppDomain?: string
  titleChunks: TransactionTitleChunk[]
  types: BasicTransactionType[]
  calls: TransactionDisplayCall[]
}

export type BatchOperationInput = Omit<
  BatchOperation,
  'operationId' | 'titleChunks'
>

type OrgRegularTransactionProperties = {
  isSponsored: boolean
  isMerkleTransaction: false
  types: BasicTransactionType[]
  nonce: string
  callData: string
  callGasLimit: string
  verificationGasLimit: string
  preVerificationGas: string
  maxFeePerGas: string
  maxPriorityFeePerGas: string
  lightHashCallGasLimit: string
  lightHashVerificationGasLimit: string
  lightHashPreVerificationGas: string
  lightHashMaxPriorityFeePerGas: string
  signature: string
  entryPointAddress: string
  userOpHash: string
  transactionHash: string | null
  walletConnectAppName: string | null
  walletConnectAppIcon: string | null
  walletConnectSessionRequestId: string | null
  walletConnectSessionRequestTopic: string | null
  merkleTransactionId: string | null
  factory: string | null
  factoryData: string | null
  paymaster: string
  paymasterData: string
  paymasterVerificationGasLimit: string
  paymasterPostOpGasLimit: string
  lightHashPaymaster: string
  lightHashPaymasterVerificationGasLimit: string
  lightHashPaymasterPostOpGasLimit: string
  transactionData: {
    transferData?: TransferData
    swapData?: SwapData
    bridgeData?: BridgeData
    earnData?: EarnData
  }
  transactionMetadata: MetadataPayloads[]
  bridgeExternalAccountName?: string | null
}

type OrgMerkleTransactionProperties = {
  isSponsored: boolean
  isMerkleTransaction: true
  types: (
    | BasicTransactionType.UpdateSigners
    | BasicTransactionType.AddNetworks
    | BasicTransactionType.RecoverAccount
    // Workspace auto-earn
    | BasicTransactionType.CustomTxn
  )[]
  nonce: null
  callData: null
  callGasLimit: null
  verificationGasLimit: null
  preVerificationGas: null
  maxFeePerGas: null
  maxPriorityFeePerGas: null
  lightHashCallGasLimit: null
  lightHashVerificationGasLimit: null
  lightHashPreVerificationGas: null
  lightHashMaxPriorityFeePerGas: null
  signature: null
  entryPointAddress: null
  userOpHash: null
  transactionHash: null
  walletConnectAppName: null
  walletConnectAppIcon: null
  walletConnectSessionRequestId: null
  walletConnectSessionRequestTopic: null
  merkleTransactionId: null
  factory: null
  factoryData: null
  paymaster: null
  paymasterData: null
  paymasterVerificationGasLimit: null
  paymasterPostOpGasLimit: null
  lightHashPaymaster: null
  lightHashPaymasterVerificationGasLimit: null
  lightHashPaymasterPostOpGasLimit: null
  txData: { transactions: MerkleTransactionData[] }
  bridgeExternalAccountName: null
}

export type OrgRegularTransaction = CommonOrgTransactionProperties &
  OrgRegularTransactionProperties

export type OrgMerkleTransaction = CommonOrgTransactionProperties &
  OrgMerkleTransactionProperties

export type OrgTransaction = CommonOrgTransactionProperties &
  (OrgMerkleTransactionProperties | OrgRegularTransactionProperties)

export const PUBLIC_TRANSACTION_FIELDS = [
  'id',
  'createdAt',
  'updatedAt',
  'submittedAt',
  'transactionTime',
  'smartAccountId',
  'smartAccountAddress',
  'chainId',
  'memo',
  'properties',
  'nonce',
  'callData',
  'callGasLimit',
  'verificationGasLimit',
  'preVerificationGas',
  'maxFeePerGas',
  'maxPriorityFeePerGas',
  'paymaster',
  'entryPointAddress',
  'userOpHash',
  'transactionHash',
  'status',
  'options',
  'calls',
  'isPersonalOrg',
  'name',
  'isSplitsTransaction',
  'statusOrder',
  'types',
  'isMerkleTransaction',
  'chainIds',
  'walletConnectAppName',
  'walletConnectAppIcon',
  'isSponsored',
  'gasPaidBy',
  'factory',
  'factoryData',
  'paymasterData',
  'paymasterPostOpGasLimit',
  'paymasterVerificationGasLimit',
  'lightHashCallGasLimit',
  'lightHashMaxPriorityFeePerGas',
  'lightHashPaymaster',
  'lightHashPaymasterPostOpGasLimit',
  'lightHashPaymasterVerificationGasLimit',
  'lightHashPreVerificationGas',
  'lightHashVerificationGasLimit',
  'usdValue',
  'usdDisplayValue',
  'usdNetValue',
  'displayTokenAddress',
  'displayChainId',
  'displayTokenAmount',
  'bridgeExternalAccountName',
  'titleChunks',
  'batchOperations',
  'transactionRecipients',
] satisfies Array<keyof OrgTransaction>

export type PublicRegularTransaction = Pick<
  OrgRegularTransaction,
  (typeof PUBLIC_TRANSACTION_FIELDS)[number]
> & {
  isPublicTransaction: true
  signers: Signer[]
  eoaSigners: EOASigner[]
  transactionMetadata?: MetadataPayloads[]
  simulation?: { simulationURL: string }
  decodedCalls?: Array<BackendTransactionDecodedCall>
}
export type PublicMerkleTransaction = Pick<
  OrgMerkleTransaction,
  (typeof PUBLIC_TRANSACTION_FIELDS)[number]
> & {
  txData: OrgMerkleTransaction['txData']
  isPublicTransaction: true
  signers: Signer[]
  eoaSigners: EOASigner[]
  childTransactions: Array<PublicRegularTransaction>
}

export type PublicTransaction =
  PublicRegularTransaction | PublicMerkleTransaction

export type BackendTransaction = (
  PublicTransaction | OrgTransaction | OrgMerkleTransaction
) & {
  // The account's EOA signers. Only the single-transaction endpoint returns it.
  smartAccountEoaSigners?: OrgEOASigner[]
}

type OrgMessageSigner = OrgSigner & {
  signedAt: string
}

type OrgMessageEOASigner = OrgEOASigner & {
  signedAt: string
}

export type OrgMessage = {
  id: string
  createdAt: string
  updatedAt: string
  name: string
  message: Hex
  dataType: 'PERSONAL_SIGN' | 'ETH_SIGNTYPEDDATA_V4'
  smartAccountId: string
  smartAccountAddress: IAddress
  chainId: SupportedChainId
  options: string
  createdByUserId?: string
  createdByUserEmail?: string
  walletConnectAppName?: string
  walletConnectAppIcon?: string
  walletConnectSessionRequestId?: string
  walletConnectSessionRequestTopic?: string
  signers: OrgMessageSigner[]
  eoaSigners: OrgMessageEOASigner[]
  signature: string
}

// A null tokenAddress allows all tokens on the chain; an address allows one
// token. The rules for one contact combine with OR, and tokenAddress is the
// discriminant: a contact's rules are all networks or all tokens, never mixed.
export type NetworkSendRule = {
  chainId: SupportedChainId
  tokenAddress: null
}

export type TokenSendRule = {
  chainId: SupportedChainId
  tokenAddress: EvmAddress.Type
}

export type ContactSendRule = NetworkSendRule | TokenSendRule

export type OrgContact = {
  address: Address.Type
  orgLabel: string
  // An empty list means the contact accepts any token on any chain.
  sendRules: ContactSendRule[]
  updatedAt: string
}

export enum PostOnboardingChecklistStepKey {
  Fund = 'FUND',
  AddTeammate = 'ADD_TEAMMATE',
  AutoEarn = 'AUTO_EARN',
}

export type PostOnboardingChecklistStep = {
  key: PostOnboardingChecklistStepKey
  completedAt: string | null
}

// The backend is the only writer: steps complete automatically and permanently
// from org facts. The FE renders; it never computes checks.
export type PostOnboardingChecklist = {
  id: string
  orgId: string
  completedAt: string | null
  // Whether the requesting user hid the checklist for themselves. Hiding is
  // per-user and never changes what other members see.
  hidden: boolean
  steps: PostOnboardingChecklistStep[]
}

export enum NFTTokenType {
  ERC721,
  ERC1155,
}

export enum NFTMediaType {
  video,
  image,
  audio,
}

export type NFTMetadata = {
  address: IAddress
  chainId: SupportedChainId
  name: string
  symbol: string
  type: NFTTokenType
  imageURI?: string
  mediaURI?: string
  mediaType?: NFTMediaType
  openseaURL?: string
  collectionName?: string
}

export type NFTTokenBalance = NFTMetadata & {
  account: IAddress
  tokenId: bigint
  amount: bigint
}

export type OrgAssetTransfer = {
  id: string
  createdAt: string
  smartAccountId: string
  smartAccountAddress: IAddress
  chainId: SupportedChainId
  transactionHash: string
  transactionTime: string
  blockNumber: string
  assetType: AssetType
  transferType: (typeof TransferType)[keyof typeof TransferType]
  tokenAddress: string
  from: string
  to: string
  isSplitsTransaction: false
  tokenId?: string
  amount?: string
  value?: string
  memo?: string
  properties?: Record<string, unknown> | null
  onrampSourceName?: string
  nftSymbol?: string
  nftName?: string
  titleChunks: TransactionTitleChunk[] | null
}

export const AssetType = {
  NATIVE: 'NATIVE',
  ERC20: 'ERC20',
  ERC721: 'ERC721',
  ERC1155: 'ERC1155',
} as const
export type AssetType = (typeof AssetType)[keyof typeof AssetType]

export const TransferType = {
  Inbound: 'Inbound',
  Outbound: 'Outbound',
  Mix: 'Mix',
} as const

export type AddressSearchResult = {
  address: IAddress
  orgLabel?: string
  smartAccountName?: string
  farcaster?: {
    username: string
    fid: number
    pfp?: string
    displayName?: string
  }
  ens?: { name: string; avatar?: string }
  payee?: Pick<
    CompliantPaymentRelation,
    'payeeOrg' | 'payeeInvitedUserId' | 'payeeInvitedEmail'
  >
  bankAccount?: {
    externalAccountId: string
    name: string
    accountNumberLastFour: string
  }
}

export type BackendLoggingData = {
  [key: string]: undefined | string | number | boolean | string[]
}

export type TokenVisibilityEntry = {
  orgId: string
  chainId: SupportedChainId
  address: IAddress
  // False when the org has an override for a token it does not hold. Absent
  // from older servers, which reads as held.
  held?: boolean
  visible: boolean
  globalSpamData?: {
    isSpam: boolean
    spamData: SpamData
  }
}

export type SpamData = {
  price?: number
  reasoning?: string
  score?: number
}

export type SpamDataV2Source = 'short_circuit' | 'llm' | 'admin_override'

export type SpamDataV2 = {
  version: 2
  source: SpamDataV2Source
  confidence: number
  reasoning: string
  price?: number
  tokenInfo?: {
    gt_score?: number
    gt_score_details?: Record<string, unknown>
  }
}

export const isSpamDataV2 = (
  data: SpamData | SpamDataV2 | null | undefined,
): data is SpamDataV2 =>
  data !== null && data !== undefined && 'version' in data && data.version === 2

export type NFTBlockType = 'ContractAddress' | 'TokenId'

export type NFTVisibilityEntry = {
  orgId: string
  chainId: SupportedChainId
  blockType: NFTBlockType
  address: IAddress
  tokenId: string
  nftType: string
  // Display data for an entry with no balance row. Optional because the
  // server may omit them, null when the stored row has none.
  name?: string | null
  symbol?: string | null
  thumbnailUrl?: string | null
  // False when the org has a token-id override it holds no balance for.
  // Absent from older servers, which reads as held.
  held?: boolean
  visible: boolean
  globalSpamData?: {
    isSpam: boolean
    spamData: SpamData
  }
}

export type TokenContract = {
  symbol: string
  decimals: number
  tokenURI?: string
}

export type TokenFeatures = {
  privateTransfers: boolean
}

export type TokenFeaturesResponse = {
  tokens: {
    chainId: number
    address: Address.Type
    features: TokenFeatures
  }[]
}

export type ScheduleInterval = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY'

export type Schedule = {
  id: string
  orgId: string
  smartAccountId: string
  smartAccountAddress: IAddress
  name: string
  memo?: string
  interval: ScheduleInterval
  startDate: string
  nextExecutionDate: string
  isPaused: boolean
  createdByUser: {
    id: string
    email: string
    displayName?: string
    profilePhotoUrl?: string
  } | null
  recipients: {
    id: string
    address: IAddress
    amount: string
  }[]
  outputTokenAddress: IAddress
  outputTokenChainId: string
}

export type InvoiceStatus =
  'CREATED' | 'EMAIL_SENT' | 'EMAIL_NOT_SENT' | 'PAID' | 'VOID'

export type InvoicePayment = {
  id: string
  chainId: number
  transactionHash: string
  transactionTime: string | null
  tokenAddress: string | null
  amount: string | null
  value: string | null
  from: string | null
  titleChunks: TransactionTitleChunk[] | null
}

export type Invoice = {
  id: string
  orgId: string
  invoiceScheduleId?: string | null
  smartAccountId: string
  smartAccountAddress: IAddress
  invoiceNumber: number
  payeeName: string
  payerName: string
  payerEmail?: string | null
  ccEmails?: string[]
  chainId: number
  tokenAddress: IAddress
  amount: string
  dateSent: string
  dueDate: string
  memo?: string | null
  allowFiatPayments: boolean
  attachments: Array<{ url: string; fileName: string; fileExtension: string }>
  status: InvoiceStatus
  createdAt: string
  updatedAt: string
  payments: InvoicePayment[]
}

// ONCE = a one-off invoice deferred to its issue date: the schedule executes a
// single time on startDate (creating + emailing the invoice), then is deleted.
export type InvoiceScheduleInterval =
  'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY' | 'ONCE'

export type InvoiceSchedule = {
  id: string
  createdAt: string
  updatedAt: string
  orgId: string
  smartAccountId: string
  payeeName: string
  payerName: string
  payerEmail: string | null
  ccEmails?: string[]
  chainId: number
  tokenAddress: IAddress
  amount: string
  memo: string | null
  allowFiatPayments: boolean
  attachments: Array<{ url: string; fileName: string; fileExtension: string }>
  // The first due date; sets the repeating day
  startDate: string
  interval: InvoiceScheduleInterval
  // Days before each due date that its invoice goes out
  dueDateDays: number
  // The next due date; its invoice goes out dueDateDays earlier
  nextExecutionDate: string
  // Server-computed date the next invoice actually goes out; can be later
  // than nextExecutionDate - dueDateDays when that day already passed, and
  // null when it never sends (paused, or past due and refused by the cron).
  // Absent only on cached responses from before the field existed.
  nextSendDate?: string | null
  isPaused: boolean
  invoicesCount: number
}

export type NeynarUser = {
  fid: number
  username: string
  display_name?: string | undefined
  pfp_url?: string | undefined
  follower_count: number
  following_count: number
  verifications: string[]
  bio: string | undefined
  verified_addresses: {
    eth_addresses: `0x${string}`[]
    primary: `0x${string}` | undefined
  }
}

export const SponsorshipCycleType = {
  MONTHLY: 'MONTHLY',
  LIFETIME: 'LIFETIME',
} as const
export type SponsorshipCycleType =
  (typeof SponsorshipCycleType)[keyof typeof SponsorshipCycleType]
